import { dashboardEnglish } from './dashboard.ts';
// English catalog. Namespaces not listed here fall back to zh-CN (see i18n/index.ts).
// Currently translated: common, nav, layout, routeState, dingtalk, settings.
export default {
  dashboard: dashboardEnglish,
  common: {
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    refresh: 'Refresh',
    actions: 'Actions',
    empty: 'No records in the current scope.',
    previous: 'Previous',
    next: 'Next',
    pagination: '{page} / {pages} · {total} total',
    paginationLabel: 'Table pagination',
    none: 'None',
    networkError: 'Cannot reach the service. Check your network and try again.',
    requestFailed: 'Request failed. Try again later.',
    diagnostics: 'Diagnostics',
    code: 'Code',
    traceId: 'Trace ID',
    rawMessage: 'Raw message',
    unsavedCloseConfirm: 'This form has unsaved changes. Close anyway?',
    downloadExpired: 'Download failed or expired',
  },
  nav: {
    overview: 'Overview',
    metrics: 'Metric catalog',
    'metric-import': 'Metric import',
    query: 'Metric query',
    governance: 'Review & publish',
    metadata: 'Metadata center',
    knowledge: 'Knowledge base',
    targets: 'Targets',
    tasks: 'Tasks',
    dashboards: 'Dashboard designer',
    dashboardDatasets: 'Dashboard datasets',
    sources: 'Data sources',
    'dw-builder': 'DW builder',
    'business-objects': 'Business objects',
    exports: 'Exports',
    users: 'Users & identities',
    dingtalk: 'DingTalk sync',
    platformSettings: 'Platform settings',
    roles: 'Roles & permissions',
    audit: 'Audit & access',
  },
  layout: {
    brand: '福瑞Compass',
    brandSubtitle: 'Operations Data Platform',
    eyebrow: 'COMPASS / WORKSPACE',
    connected: 'Connected',
    mainNav: 'Main navigation',
    themeToggle: 'Switch theme',
    themeMenu: 'Theme',
    themeDark: 'Dark',
    themeLight: 'Light',
    themeBlue: 'Blue',
    logout: 'Log out',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    logoutConfirmTitle: 'Log out?',
    logoutConfirmBody: 'You will need to authenticate again to continue.',
    confirmLogout: 'Log out',
    language: 'Language',
    langZh: '简体中文',
    langEn: 'English',
    accountMenu: 'Account menu',
    appVersion: 'System version',
  },
  routeState: {
    forbiddenTitle: 'Access denied',
    featureDisabledTitle: 'Business objects are not enabled',
    unavailableTitle: 'Service unavailable',
    notFoundTitle: 'Page not found',
    forbiddenMessage: 'Your account has no access to this page. Choose an available feature from the navigation.',
    featureDisabledMessage: 'This feature is not enabled in the current environment. Contact an administrator to confirm the feature flag.',
    unavailableMessage: 'Unable to verify the session or reach the service. Try again later.',
    notFoundMessage: 'Page not found. Choose a feature from the navigation.',
    backHome: 'Back to home',
    login: 'Log in',
  },
  dingtalk: {
    syncQueued: 'Sync queued. Refresh to see the latest directory once it completes.',
    mappingDenied: 'Adjusting department mappings requires organization policy permission; this account has read-only access.',
    enterprise: 'Enterprise',
    runFullSync: 'Run full sync',
    working: 'Working…',
    refresh: 'Refresh',
    unmappedUsers: 'Unmapped DingTalk users',
    colUser: 'User',
    colEmail: 'Email',
    colTitle: 'Title',
    colDepartment: 'Department',
    colCompassUser: 'Compass user',
    selectUser: 'Select user',
    bind: 'Bind',
    noUnmapped: 'No unmapped users',
    deptMappings: 'Department mappings',
    colParent: 'Parent',
    colCompassOrg: 'Compass organization',
    unmapped: 'Unmapped',
    noDepartments: 'No departments synced',
  },
  settings: {
    localTestOnly: 'Local validation only (connectivity needs manual verification)',
    title: 'Platform settings',
    subtitle: 'Manage platform connections and runtime parameters in one place',
    navAria: 'Settings namespaces',
    editable: 'Editable',
    governed: 'Governed',
    deployed: 'Deployed',
    loading: 'Loading settings…',
    sourceOverride: 'Administrator override',
    sourceEnvironment: 'Deployment environment',
    sourceAuthoritative: 'Authoritative configuration',
    closeHistory: 'Close history',
    showHistory: 'History',
    resetToInherit: 'Reset to inherited',
    refreshSettings: 'Refresh settings',
    sourceLabel: 'Source',
    recordVersion: 'Record version',
    statusLabel: 'Status',
    healthReady: 'Ready',
    healthRestart: 'Restart required',
    healthKeyUnavailable: 'Key unavailable',
    testTarget: 'Test target',
    runTest: 'Test saved settings',
    manualTestHint: 'This setting has no automated remote check; complete external validation using the runbook.',
    managedGoverned: 'This configuration is managed by a published governance policy.',
    managedDeployed: 'This configuration is managed by deployment operations.',
    openManagement: 'Open management entry',
    historyTitle: 'History',
    colRevision: 'Revision',
    colMode: 'Mode',
    colChangedFields: 'Changed fields',
    colTime: 'Time',
    colActor: 'Actor',
    colAction: 'Action',
    rollback: 'Restore',
    noHistory: 'No history yet',
    namespaceUnavailable: 'Settings namespace unavailable.',
    save: 'Save',
    jsonInvalid: 'Invalid JSON',
    noticeSaved: 'Saved',
    noticeSavedRestart: 'Saved; restart required',
    noticeRestored: 'Inherited configuration restored',
    noticeRollbackCreated: 'Restore revision created',
    errorConflict: 'Settings were modified by another administrator. Your form and restore content are kept until you refresh.',
    confirmDisableLastProvider: 'Disabling the identity provider may affect sign-in. Continue?',
    confirmRefreshDiscard: 'Refreshing discards the current form, including unsaved secret replacements. Refresh anyway?',
    confirmReset: 'Resetting restores the inherited deployment configuration. Continue?',
    confirmRollbackInherit: 'This revision represents inheritance and restores the current environment baseline. Continue?',
    confirmRollbackVersion: 'A new settings revision will be created from this historical revision. Continue?',
    confirmLeaveDirty: 'The form has unsaved changes. Leaving clears secret replacements. Continue?',
    test: {
      title: 'Test results',
      historical: 'Historical results',
      passed: 'All checks passed',
      partial: 'Partial checks completed',
      manual: 'Manual check required',
      failed: 'Check failed',
      versionPrefix: 'Version',
      envConfig: 'Environment configuration',
      defaultTarget: 'Default target',
      checkFallback: 'Check',
    },
    secret: {
      configured: 'Configured',
      notConfigured: 'Not configured',
      keepPlaceholder: 'Leave empty to keep the current value',
      enterNew: 'Enter a new value',
      clear: 'Clear',
    },
    activation: {
      runtime: 'Runtime status',
      desiredVersion: 'Target revision',
      currentProcess: 'Current process',
      envConfig: 'Environment configuration',
      scopeResponding: 'Reflects the responding process only',
      scopeUnknown: 'Process scope unknown',
    },
    dingtalk: {
      newCorp: 'New enterprise',
      corpId: 'Enterprise ID',
      corpName: 'Enterprise name',
      appKey: 'App key',
      agentId: 'Agent ID',
      appSecret: 'App secret',
      callbackToken: 'Callback token',
      aesKey: 'Message encryption key',
      removeCorp: 'Remove enterprise',
      addCorp: 'Add enterprise',
      confirmRemoveLast: 'Removing the last enterprise disables DingTalk sign-in. Continue?',
    },
  },
};
